import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { ReactComponent as CalendarIcon } from "feather-icons/dist/icons/calendar.svg";
import { ReactComponent as ClockIcon } from "feather-icons/dist/icons/clock.svg";
import { ReactComponent as ArrowRightIcon } from "feather-icons/dist/icons/arrow-right.svg";

import CoverArt from "./CoverArt";
import { CategoryBadge, MetaRow, MetaItem } from "./ui";
import { formatDate, readingTimeLabel } from "../utils/text";

/**
 * Carte d'article — reprend le style des cartes de blog du template (fond gray-100, bordure, coins arrondis)
 * avec l'image qui zoome doucement au survol et le titre qui passe en violet.
 */

// La carte se soulève légèrement au survol ; le visuel (CoverArt) gère lui-même son zoom et son reflet.
const Card = styled(Link)`
  ${tw`flex flex-col h-full bg-gray-100 rounded-lg overflow-hidden focus:outline-none focus:shadow-outline`}
  transition: transform 400ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 400ms ease;
  &:hover {
    ${tw`shadow-raised`}
    transform: translateY(-4px);
  }
`;

const ImageWrap = tw.div`relative h-56 sm:h-64 w-full overflow-hidden bg-gray-200`;

const Info = tw.div`flex-1 flex flex-col p-6 sm:p-8 border-2 border-t-0 border-gray-200 rounded-b-lg`;
const TopRow = tw.div`flex items-center justify-between flex-wrap`;
const Title = tw.h3`mt-4 font-black text-xl sm:text-2xl leading-snug text-gray-900 group-hover:text-primary-500 transition duration-300`;
const Excerpt = styled.p`
  ${tw`mt-3 text-sm text-gray-600 font-medium leading-relaxed`}
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
const Meta = tw(MetaRow)`mt-5`;
const ReadMore = styled.span`
  ${tw`mt-auto pt-6 inline-flex items-center text-sm font-bold text-primary-500`}
  svg {
    ${tw`w-4 h-4 ml-2 transition duration-300 transform`}
  }
  ${Card}:hover & svg {
    ${tw`translate-x-1`}
  }
`;

export default function PostCard({ post, showExcerpt = true, headingAs = "h3", eager = false }) {
  if (!post) return null;
  return (
    <Card to={`/blog/${post.slug}`} aria-label={post.title} className="group">
      <ImageWrap>
        <CoverArt seed={post.slug} color={post.category.color} image={post.cover.image} plain={post.cover.plain} alt="" eager={eager} />
      </ImageWrap>
      <Info>
        <TopRow>
          {/* La carte entière est déjà un lien : la pastille est un simple span pour éviter un lien imbriqué. */}
          <CategoryBadge category={post.category} linked={false} />
        </TopRow>
        <Title as={headingAs}>{post.title}</Title>
        {showExcerpt && post.excerpt && <Excerpt>{post.excerpt}</Excerpt>}
        <Meta>
          <MetaItem>
            <CalendarIcon aria-hidden="true" />
            <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
          </MetaItem>
          <MetaItem>
            <ClockIcon aria-hidden="true" />
            {readingTimeLabel(post.readingTime)}
          </MetaItem>
        </Meta>
        <ReadMore>
          Lire l'article <ArrowRightIcon aria-hidden="true" />
        </ReadMore>
      </Info>
    </Card>
  );
}
